/**
 * Règles de peuple COF2 - application des modificateurs de caractéristiques
 * à la création du personnage.
 *
 * Les peuples utilisent une notation textuelle pour les bonus/malus :
 * - "+1 PER ou CHA" -> un bonus à choisir parmi deux
 * - "-1 FOR ou CON" -> un malus à choisir parmi deux
 * - "+1 à l'une des deux plus faibles caractéristiques (au choix)" -> humain
 *
 * Ce module parse ces notations et applique le choix du joueur aux caracs.
 */

import type { ModCaracEntry } from '@data/schemas';
import { CARACS, type Carac, emptyCaracs } from './caracs';

/**
 * Formate une entree ModCaracEntry en string lisible pour l'affichage UI
 * et pour le parsing par parsePeupleChoice().
 *
 * Exemples :
 * - "+1 PER ou CHA"   (carac + alternatif)
 * - "-1 FOR ou CON"   (idem)
 * - "+1 a une des plus faibles caracteristiques (au choix)" (humain : carac=libre)
 *
 * carac=libre signale le cas humain (unique dans les 8 peuples) :
 * bonus a l'une des 2 plus faibles caracs a la creation.
 */
export function formatModCaracEntry(e: ModCaracEntry): string {
  const v =
    typeof e.valeur === 'number'
      ? e.valeur > 0
        ? `+${e.valeur}`
        : `${e.valeur}`
      : String(e.valeur);
  if (e.carac === 'libre') {
    return `${v} a une des plus faibles caracteristiques (au choix)`;
  }
  if (e.alternatif) return `${v} ${e.carac} ou ${e.alternatif}`;
  return `${v} ${e.carac}`;
}

/**
 * Une option de choix de bonus / malus parsée depuis la BDD.
 * Exemple : "+1 PER ou CHA" donne `{ value: 1, options: ['PER','CHA'] }`.
 */
export interface PeupleChoice {
  /** Valeur appliquée (positif = bonus, négatif = malus). */
  readonly value: number;
  /**
   * Caracs parmi lesquelles choisir. Si `'WEAKEST_N'`, le jeu applique le bonus
   * aux N caracs les plus faibles (cas humain).
   */
  readonly options: readonly Carac[] | 'WEAKEST_2';
  /** Texte original non parsé (pour affichage UI et debug). */
  readonly raw: string;
}

const CARAC_SET = new Set<string>(CARACS);

/**
 * Parse une chaîne "+1 PER ou CHA" ou "-1 FOR ou CON" ou "+1 FOR".
 * Retourne null si la chaîne n'est pas reconnue.
 */
export function parsePeupleChoice(raw: string): PeupleChoice | null {
  const trimmed = raw.trim();

  // Cas humain : +1 aux deux plus faibles caracs.
  // Match "plus faibles caracteristiques" ou "plus faibles caractéristiques" (avec/sans accent)
  if (/plus faibles?\s+caract[ée]ristiques?/i.test(trimmed)) {
    return { value: 1, options: 'WEAKEST_2', raw: trimmed };
  }

  // Cas "+1 PER ou CHA" / "-1 FOR ou CON"
  const multi = /^([+-]\d+)\s+([A-Z]{3})(?:\s+ou\s+([A-Z]{3}))?/i.exec(trimmed);
  if (!multi) return null;
  const value = Number.parseInt(multi[1] ?? '0', 10);
  if (!Number.isFinite(value) || value === 0) return null;

  const opts: Carac[] = [];
  for (const candidate of [multi[2], multi[3]]) {
    if (!candidate) continue;
    const up = candidate.toUpperCase();
    if (CARAC_SET.has(up)) opts.push(up as Carac);
  }
  if (opts.length === 0) return null;

  return { value, options: opts, raw: trimmed };
}

/**
 * Modificateurs de peuple bruts depuis la BDD (structure YAML riche).
 */
export interface PeupleModificateurs {
  readonly bonus?: ReadonlyArray<ModCaracEntry>;
  readonly malus?: ReadonlyArray<ModCaracEntry>;
}

/**
 * Choix effectifs du joueur pour appliquer les modificateurs.
 * Chaque entrée référence l'index de l'option dans `PeupleModificateurs.options`
 * ou `malus`, plus la carac choisie (si l'option offre un choix).
 */
export interface PeupleChoiceResolution {
  /** Pour chaque option, la carac retenue. Pour le cas humain : tableau de 2 caracs. */
  readonly optionsChoices: ReadonlyArray<Carac | readonly [Carac, Carac]>;
  /** Pour chaque malus (s'il y en a), la carac retenue. */
  readonly malusChoices: readonly Carac[];
}

/**
 * Applique les choix de peuple aux caracs de base et retourne les modificateurs.
 *
 * Retourne un `Caracs` contenant uniquement les deltas (0 pour les caracs non affectées).
 *
 * @throws Error si les choix ne correspondent pas aux options du peuple
 *   (mauvais nombre, carac hors options, etc.).
 */
export function applyPeupleChoices(
  mod: PeupleModificateurs,
  choices: PeupleChoiceResolution,
): Record<Carac, number> {
  const delta = emptyCaracs();
  const bonus = mod.bonus ?? [];
  const malus = mod.malus ?? [];

  if (choices.optionsChoices.length !== bonus.length) {
    throw new Error(
      `Peuple : ${bonus.length} options attendues, ${choices.optionsChoices.length} fournies.`,
    );
  }
  if (choices.malusChoices.length !== malus.length) {
    throw new Error(
      `Peuple : ${malus.length} malus attendus, ${choices.malusChoices.length} fournis.`,
    );
  }

  bonus.forEach((entry, i) => {
    const parsed = parsePeupleChoice(formatModCaracEntry(entry));
    if (!parsed) return;
    const userChoice = choices.optionsChoices[i];
    applyChoice(delta, parsed, userChoice);
  });

  malus.forEach((entry, i) => {
    const parsed = parsePeupleChoice(formatModCaracEntry(entry));
    if (!parsed) return;
    const userChoice = choices.malusChoices[i];
    if (userChoice) applyChoice(delta, parsed, userChoice);
  });

  return delta;
}

function applyChoice(
  delta: Record<Carac, number>,
  parsed: PeupleChoice,
  userChoice: Carac | readonly [Carac, Carac] | undefined,
): void {
  if (parsed.options === 'WEAKEST_2') {
    if (!Array.isArray(userChoice) || userChoice.length !== 2) {
      throw new Error("Peuple humain : il faut choisir 2 caracs pour '+1 aux deux plus faibles'.");
    }
    const [a, b] = userChoice as readonly [Carac, Carac];
    if (a === b) {
      throw new Error('Peuple humain : les deux caracs choisies doivent être différentes.');
    }
    delta[a] += parsed.value;
    delta[b] += parsed.value;
    return;
  }

  if (typeof userChoice !== 'string') {
    throw new Error(`Peuple : une seule carac attendue pour "${parsed.raw}".`);
  }
  if (!parsed.options.includes(userChoice as Carac)) {
    throw new Error(
      `Peuple : "${userChoice}" n'est pas une option valide pour "${parsed.raw}". Options : ${parsed.options.join(', ')}.`,
    );
  }
  delta[userChoice as Carac] += parsed.value;
}

/**
 * Calcule les indices des 2 caracs les plus faibles dans un snapshot donné.
 * En cas d'égalité, on prend par ordre canonique CARACS.
 * Utilitaire pour proposer une valeur par défaut au joueur humain.
 */
export function weakestTwoCaracs(caracs: Readonly<Record<Carac, number>>): [Carac, Carac] {
  const sorted = [...CARACS].sort((a, b) => {
    const diff = caracs[a] - caracs[b];
    if (diff !== 0) return diff;
    return CARACS.indexOf(a) - CARACS.indexOf(b);
  });
  return [sorted[0] as Carac, sorted[1] as Carac];
}
